<script lang="ts" setup>
    import _ from 'lodash';
    import TreeItem from '../../components/TreeItem.vue';
    import { IAlertTreeOptionModel, constantAlertIds } from '../../lib/Models/Comments/Alerts';

    const props = withDefaults(defineProps<{
        alertOptions: IAlertTreeOptionModel[],
        readonly?: boolean,
        selectedAlertOptions: number[]
    }>(), {
        readonly: false
    });

    const emit = defineEmits<{
        (e: "changeAlertSelection", checked: boolean, treeItem: IAlertTreeOptionModel): void,
        (e: "update:selectedAlertOptions", selectedAlertOptions: number[]): void
    }>();
    
    function onClickAlertItem(check: boolean, item: IAlertTreeOptionModel)
    {
        item.selected = check;

        // 'All' option was checked
        if (check && constantAlertIds.All === item.id)
        {
            for(const i of props.alertOptions)
            {
                i.selected = false;
    
                for(const c of i.children ?? [])
                {
                    c.selected = false;
                }
            }
            emit("update:selectedAlertOptions", [constantAlertIds.All]);
            return;
        }

        const tempSlectedAllItem = new Set<number>(props.selectedAlertOptions);

        // Something other than 'All' was checked
        if (constantAlertIds.All !== item.id)
        {
            tempSlectedAllItem.delete(constantAlertIds.All);

            if (check)
                tempSlectedAllItem.add(item.id);
            else
                tempSlectedAllItem.delete(item.id);
        }

        if (item.children)
        {
            for(const child of item.children)
            {
                if (check)
                    tempSlectedAllItem.add(child.id);
                else
                    tempSlectedAllItem.delete(child.id);
            }
        }

        if (item.parent)
        {
            // If unchecking, uncheck parent
            if (!check)
            {
                //item.parent.selected = false;
                tempSlectedAllItem.delete(item.parent.id);
            }

            // If checking a child level, see if all childern are checked
            if (check)
            {
                if (item.parent)
                {
                    const siblingItems = item.parent.children;
                    if (siblingItems)
                    {
                        const siblingItemsIds = siblingItems.map(item => item.id);
                        const intersection = _.intersection(siblingItemsIds, Array.from(tempSlectedAllItem.values()));

                        if (intersection.length === siblingItemsIds.length)
                            tempSlectedAllItem.add(item.parent.id);
                    }
                }
            }
        }

        // Update the selected item list
        emit("update:selectedAlertOptions", Array.from(tempSlectedAllItem.values()));
    }

    function isItemChecked(treeItem: IAlertTreeOptionModel): boolean
    {
        if (!props.selectedAlertOptions)
            return false;

        // Are we on a lower level item?
        if (!treeItem.children)
            return undefined !== props.selectedAlertOptions.find(selId => selId === treeItem.id);
        
        // Id we're on an upper level item check if all the lower level items are checked
        const childItems = treeItem.children;
        if (childItems)
        {
            const childItemsIds = childItems.map(item => item.id);
            const intersection = _.intersection(childItemsIds, props.selectedAlertOptions);

            if (intersection.length === childItemsIds.length)
                return true;
        }
        return false;
    }

</script>

<template>
    <TreeItem v-for="item in props.alertOptions" :treeItem="item">
        <template #item="{treeItem}">
            <input type="checkbox" 
                :readonly="readonly"
                :checked="isItemChecked(<IAlertTreeOptionModel>treeItem)"
                @change="val => onClickAlertItem((<HTMLInputElement>val.target)?.checked, <IAlertTreeOptionModel>treeItem)" />
            {{treeItem?.name}}
        </template>                  
    </TreeItem>
</template>